import { Link } from 'react-router-dom'
import Breadcrumb from '@/components/ui/Breadcrumb'
import Search from '@/components/ui/Search'
import FilterParamsDisplay from '@/components/ui/FilterParamsDisplay'
import Pagination from '@/components/ui/Pagination'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useSearchFilterParams } from '@/hooks/useSearchFilterParams'
import { useFilterEvents, useSearchEvents } from '@/lib/api/hooks/event'
import { useAllWebPages } from '@/lib/api/hooks/web-pages'
import FullPageSpinner from '@/components/ui/FullPageSpinner'
import type { Event } from '@/lib/api/types'
import { motion } from 'framer-motion'
import { fadeUp, staggerContainer, viewportReveal } from '@/lib/motion'
import { parseHtmlString } from '@/utils/helpers/html'
import FILTER_SECTIONS from '@/components/pages/Events/FilterModule/FILTER_SECTIONS'
import FilterModule from '@/components/ui/FilterModule'

// ─── Component ───────────────────────────────────────────────────────────────

export default function Events() {
  const { data, isFetching, isError, refetch } = useAllWebPages()
  const eventsWebpageData =
    data?.data?.find(
      page =>
        page.codeName?.toLowerCase() === 'events' ||
        page.websitePageName?.toLowerCase() === 'events'
    )?.content ?? null

  // ── URL-driven search / filter state ──────────────────────────────────────
  const {
    mode,
    searchTerm,
    filterState,
    page,
    size,
    setSearch,
    setFilters,
    removeFilter,
    clearFilters,
    setPage,
    apiFilters,
    getActiveFilterCount
  } = useSearchFilterParams(FILTER_SECTIONS)

  // ── Always-on hero fetch (not affected by search/filter mode) ─────────────
  const { data: upcomingEventsData } = useFilterEvents(
    { page: 1, size: 1 },
    { upcoming: true }
  )

  // ── Search query ─────────────────────
  const { data: searchData, isLoading: isSearchLoading, error: searchError } = useSearchEvents(
    searchTerm,
    { page, size }
  )

  // ── Filter / default query ─────────────────────────────────────────────────
  const { data: filterData, isLoading: isFilterLoading, error: filterError } = useFilterEvents(
    { page, size },
    apiFilters
  )

  // ── Select the right data source based on mode ────────────────────────────
  const eventsData = mode === 'search' ? searchData : filterData
  const isLoading   = mode === 'search' ? isSearchLoading : isFilterLoading
  const error       = mode === 'search' ? searchError     : filterError

  const events        = eventsData?.data?.data ?? []
  const upcomingEvent = upcomingEventsData?.data?.data?.[0]
  const totalPages    = eventsData?.data?.totalPage ?? 1
  const activeFilterCount = getActiveFilterCount(FILTER_SECTIONS) 
  const emptyMessage = mode === 'search' ? 'No events found for your search.' : mode === "filter" ? 'No events found for the selected filters.' : 'No events has been added'

  // Format date helper
  const formatDate = (dateTimeString: string) => {
    try {
      const date = new Date(dateTimeString)
      const day = date.getDate()
      const month = date.toLocaleDateString('en-GB', { month: 'short' })
      const year = date.getFullYear().toString().slice(-2)
      return `${day} ${month} '${year}`
    } catch {
      return dateTimeString
    }
  }

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Events', href: '', active: true }
  ]



  if (isFetching) {
    return <FullPageSpinner />;
  }

  if (isError) {
    return <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
      <p>Error loading page.</p>
      <button onClick={() => refetch()} className="px-4 py-2 bg-forestGreen-100 text-white rounded">Retry</button>
    </div>;
  }

  if (!eventsWebpageData) {
    return <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
      <p>Page content not found.</p>
    </div>;
  }

  return (
    <motion.div
      className="bg-white min-h-screen"
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
    >
      {/* Breadcrumb */}
      <motion.div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" variants={fadeUp}>
        <Breadcrumb items={breadcrumbItems} />
      </motion.div>

      {/* Hero Section */}
      {upcomingEvent && (
        <motion.div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8" variants={fadeUp}>
          <div className="relative w-full h-[502px] overflow-hidden">
            <img
              src={upcomingEvent.imageUrl}
              alt={upcomingEvent.eventTitle}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-25 backdrop-blur-[1px]"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-black bg-opacity-25 backdrop-blur-[20px]">
              <div className="max-w-4xl">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <div className="h-14 flex items-start">
                      <span className="text-white text-base font-bold">UPCOMING EVENT</span>
                    </div>
                    <h1 className="text-white text-3xl font-normal font-['PT_Serif'] leading-10 max-w-[820px]">
                      {upcomingEvent.eventTitle}
                    </h1>
                    <span className="text-white text-base font-bold">{upcomingEvent.datePublished ? formatDate(upcomingEvent.datePublished) : ""}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Main Content */}
      <motion.div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pb-16" variants={fadeUp}>
        {/* Page Header */}
        <div className="flex flex-col gap-10 mb-10">
          <div className="flex flex-col gap-6">
            <h2 className="text-4xl font-normal font-['PT_Serif'] leading-[48px] text-gray-900">{eventsWebpageData?.EVENT?.title ?? "Events"}</h2>
            <div className="flex flex-col gap-8">
              <p className="text-lg text-gray-900 max-w-[611px]">
                <span dangerouslySetInnerHTML={parseHtmlString(eventsWebpageData?.EVENT?.description ?? "Press releases from the Africa Space Agency, including updates from its governing bodies, technical departments, and strategic initiatives.")} />
              </p>
            </div>
          </div>

          {/* Search + Filter Controls */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center relative gap-4 justify-between flex-wrap">
              <Search
                value={searchTerm}
                onSearch={setSearch}
                placeholder="Search events..."
              />
               <FilterModule
                    sections={FILTER_SECTIONS}
                    activeFilters={filterState}
                    onApply={setFilters}
                    activeFilterCount={activeFilterCount}
                  />
            </div>

            {/* Active filter chips — only shown in filter mode */}
            <FilterParamsDisplay
              sections={FILTER_SECTIONS}
              filterState={filterState}
              onRemove={removeFilter}
              onClearAll={clearFilters}
            />
          </div>

            {/* Featured Event Card — shows first result when available */}
            {events.length > 0 && (
              <div className="relative h-96 rounded-lg overflow-hidden">
                <img
                  src={events[0].imageUrl}
                  alt={events[0].eventTitle}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-25 backdrop-blur-[1px]" />
                <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end">
                  <div className="flex flex-col gap-2">
                    {/* <div className="h-14 flex items-start">
                      <span className="text-white text-base font-bold">UPCOMING EVENT</span>
                    </div> */}
                    <h3 className="text-white text-3xl font-normal font-['PT_Serif'] leading-loose">
                      {events[0].eventTitle}
                    </h3>
                    <span className="text-white text-base font-bold">
                      {events[0].datePublished ? formatDate(events[0].datePublished) : ''}
                    </span>
                  </div>
                </div>
              </div>
            )}

          {/* Events Grid */}
          <div className="flex flex-col gap-8">
            {isLoading ? (
              <LoadingSpinner message="Loading events..." className="py-20" />
            ) : error ? (
              <div className="text-center py-20">
                <p className="text-sm text-red-500">Error loading events. Please try again later.</p>
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-sm text-gray-500">{emptyMessage}</p>
              </div>
            ) : (
              <>
                <motion.div
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5"
                  variants={staggerContainer}
                  initial="hidden"
                  whileInView="visible"
                  viewport={viewportReveal}
                >
                  {events.map((event: Event) => (
                    <motion.div
                      key={event.eventId}
                      className="w-full flex flex-col hover:shadow-lg transition-shadow"
                      variants={fadeUp}
                      whileHover={{ y: -4 }}
                      transition={{ type: 'spring', stiffness: 280, damping: 24 }}
                    >
                      <Link
                        to={`/events/${event.eventId}`}
                        className="flex flex-col cursor-pointer"
                      >
                  

                           {
                          event.imageUrl ?        <img
                          src={event.imageUrl}
                          alt={event.eventTitle}
                          className="w-full h-48 object-cover"
                          onError={(e) => {
                            // Fallback image if loading fails
                            e.currentTarget.src = 'https://placehold.co/600x400/E5E7EB/6B7280?text=No+Image'
                          }}
                        /> : <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                          <span className="text-gray-500">No Image</span>
                        </div>
                        }
                        <div className="px-4 py-6 bg-gray-50 flex flex-col gap-4">
                          <div className="w-full flex flex-col gap-4">
                            <h3 className="h-24 text-lg font-normal font-['PT_Serif'] leading-relaxed text-gray-900 hover:text-wine-100 transition-colors">
                              {event.eventTitle}
                            </h3>
                            <span className="text-base font-medium text-gray-900">{event.datePublished ? formatDate(event.datePublished) : ""}</span>
                          </div>
                        </div>
                      </Link>
                      {/* Add to Calendar Button */}

                    </motion.div>
                  ))}
                </motion.div>

                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={p => {
                      setPage(p)
                      window.scrollTo({ top: 0, behavior: 'smooth' })
                    }}
                  />
              
              </>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}